/**
 * RatificationNextSteps — wayfinding between "accepted" and "Plan of Record".
 *
 * Accepting a proposal is not the last governance act: the returning ball must
 * be taken, the loop closed, and only then can the ratified priority list be
 * published as the Plan of Record. Each of those gates is correct; before this
 * component the only way to *discover* them was to trip them one at a time
 * ("Grab the ball first", "loop must be closed"). Findings F4,
 * docs/9-exploration/MOSSO-DEMO-FINDINGS-2026-08-25.md.
 *
 * The checklist is additive — it weakens nothing. Every step's state is derived
 * from live data (see `ratificationSteps.ts`), so a step that migration 692
 * already performs server-side renders as done rather than as busywork.
 */

import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, Loader2, ArrowRight, Lock } from 'lucide-react';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useRatificationSteps, type RatificationStepKey } from './ratificationSteps';

// ─── Component ────────────────────────────────────────────────────────────────

export interface RatificationNextStepsProps {
  cycleId: string;
  loopId: string | null | undefined;
  /** Heading copy — the inbox landing and the governance page frame it differently. */
  title?: string;
  /** Hide once every step is done (the ratified-moment landing wants this). */
  hideWhenComplete?: boolean;
  className?: string;
}

export function RatificationNextSteps({
  cycleId,
  loopId,
  title = 'What happens next',
  hideWhenComplete = true,
  className = '',
}: RatificationNextStepsProps) {
  const userId = useUserStore((s) => s.userId);
  const { steps, isLoading, isComplete, refresh } = useRatificationSteps(cycleId, loopId);

  const claimBall = useCatchballLoopStore((s) => s.claimBall);
  const closeLoop = useCatchballLoopStore((s) => s.closeLoop);
  const fetchLoops = useCatchballLoopStore((s) => s.fetchFromSupabase);

  const [busyStep, setBusyStep] = useState<RatificationStepKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runStep = useCallback(
    async (key: RatificationStepKey) => {
      if (!loopId || !userId) return;
      setBusyStep(key);
      setError(null);
      try {
        // The loop actions read the loop out of the store; on the governance and
        // report pages it may never have been fetched.
        if (!useCatchballLoopStore.getState().getLoop(loopId)) {
          await fetchLoops(cycleId);
        }
        const ok =
          key === 'take_ball'
            ? await claimBall(loopId, userId)
            : key === 'close_loop'
              ? await closeLoop(loopId, userId)
              : false;
        if (!ok) {
          setError(useCatchballLoopStore.getState().error ?? 'That step could not be completed.');
        }
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'That step could not be completed.');
      } finally {
        setBusyStep(null);
      }
    },
    [loopId, userId, cycleId, claimBall, closeLoop, fetchLoops, refresh],
  );

  if (!loopId) return null;
  if (hideWhenComplete && isComplete && !isLoading) return null;

  return (
    <div
      className={` border border-[var(--rule)] bg-[var(--bg-raised)] p-4 dark:border-[var(--rule)] dark:bg-[var(--bg-raised)] ${className}`}
      data-testid="ratification-next-steps"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{title}</h3>
        {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--text-ter)]" />}
      </div>
      <p className="mt-0.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        Ratifying is not publishing. These steps remain before the list becomes the organization’s published priorities.
      </p>

      {error && (
        <p className="mt-3 border border-[var(--coral)] bg-[var(--coral-dim)] px-3 py-2 text-xs text-[var(--coral)]">
          {error}
        </p>
      )}

      <ol className="mt-3 space-y-2">
        {steps.map((step) => {
          const actionable = !step.done && step.blockedReason == null;
          const inPlace = actionable && (step.key === 'take_ball' || step.key === 'close_loop');
          return (
            <li
              key={step.key}
              data-step={step.key}
              data-done={step.done ? 'true' : 'false'}
              className="flex items-start gap-3 border border-transparent px-2 py-1.5 hover:border-[var(--rule)] dark:hover:border-[var(--rule)]"
            >
              {step.done ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--green)]" aria-hidden />
              ) : step.blockedReason ? (
                <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--text-ter)]" aria-hidden />
              ) : (
                <Circle className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" aria-hidden />
              )}
              <div className="min-w-0 flex-1">
                <p
                  className={`text-sm font-medium ${
                    step.done
                      ? 'text-[var(--text-ter)] line-through dark:text-[var(--text-sec)]'
                      : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'
                  }`}
                >
                  {step.label}
                </p>
                <p className="mt-0.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                  {step.blockedReason ?? step.description}
                </p>
              </div>
              {!step.done && step.blockedReason == null && (
                <div className="flex-shrink-0">
                  {inPlace ? (
                    <button
                      type="button"
                      onClick={() => void runStep(step.key)}
                      disabled={busyStep != null}
                      className="inline-flex items-center gap-1 bg-primary-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                    >
                      {busyStep === step.key ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : null}
                      {step.actionLabel}
                    </button>
                  ) : step.href ? (
                    <Link
                      to={step.href}
                      className="inline-flex items-center gap-1 border border-[var(--rule)] px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-pri)]"
                    >
                      {step.actionLabel ?? 'Open'}
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  ) : null}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default RatificationNextSteps;
